import { Component, model, output, signal } from '@angular/core';
import { UiButtonComponent, UIIconComponent } from '@design-system';
import { KeyboardStepMode } from './keyboard.model';

@Component({
	selector: 'app-keyboard',
	imports: [UiButtonComponent, UIIconComponent],
	templateUrl: './keyboard.component.html',
	styleUrl: './keyboard.component.scss',
	host: {
		class: 'app-keyboard block w-full',
		'[class.app-keyboard--fixed]': 'positionFixed()'
	}
})
export class KeyboardComponent {
	readonly value = signal('');
	readonly stepMode = model<KeyboardStepMode>(KeyboardStepMode.Amount);
	readonly stepValue = model<string | number>('10000');
	readonly positionFixed = model(true);

	readonly valueChange = output<string>();
	readonly confirmEvent = output<void>();
	readonly cancelEvent = output<void>();

	protected readonly layout = [
		['1', '2', '3'],
		['4', '5', '6'],
		['7', '8', '9']
	];

	setValue(value: string): void {
		this.value.set(value);
		this.valueChange.emit(value);
	}

	press(key: string): void {
		this.setValue(this.value() + key);
	}

	increase(): void {
		this.step(1);
	}

	decrease(): void {
		this.step(-1);
	}

	private step(direction: 1 | -1): void {
		const delta = this.stepDelta();
		if (delta === 0 || !Number.isFinite(delta)) {
			return;
		}

		this.setValue(Math.max(0, +this.value() + direction * delta).toString());
	}

	private stepDelta(): number {
		if (this.stepMode() === KeyboardStepMode.Percent) {
			return Math.round((+this.value() * +this.stepValue()) / 100);
		}
		return +this.stepValue();
	}

	delete(): void {
		this.setValue(this.value().slice(0, -1));
	}

	deleteAll(): void {
		this.setValue('');
	}

	confirm(): void {
		this.confirmEvent.emit();
	}

	cancel(): void {
		this.cancelEvent.emit();
	}

	formatWithSeparator(value: string | number): string {
		return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	}

	stepLabel(prefix: '+' | '-'): string {
		const percentMode = this.stepMode() === KeyboardStepMode.Percent;
		// A percent step keeps whatever precision it was configured with; a rial one is whole.
		const step = percentMode ? this.stepValue() : Math.round(Number(this.stepValue()) || 0);
		return `${prefix}${this.formatWithSeparator(step)}${percentMode ? '%' : ''}`;
	}
}
